import { ENEMY_ROLES } from "../content/enemies";
import type { EnemyRoleId } from "../content/schemas";

export type DamageAttackKind = "melee" | "projectile" | "mine" | "volatile" | "unknown";

/** Last inbound hit that damaged the player — used for death clarity. */
export type LastDamageSource = {
  enemyRole: EnemyRoleId | null;
  enemyName: string;
  elite: boolean;
  isAce: boolean;
  attackKind: DamageAttackKind;
  /** Short attack label, e.g. "ram", "bolts", "mine". */
  attackLabel: string;
  distanceM: number;
  /** Total damage of the blow before absorb splits. */
  damage: number;
  shieldAbsorbed: number;
  armorAbsorbed: number;
  hullDamage: number;
};

export type DamageSourceInput = {
  x: number;
  y: number;
  role?: EnemyRoleId | null;
  elite?: boolean;
  isAce?: boolean;
  isArenaGuardian?: boolean;
  attackKind?: DamageAttackKind;
  attackLabel?: string;
};

export function enemyDisplayName(
  role: EnemyRoleId | null | undefined,
  elite: boolean,
  isAce: boolean,
  isArenaGuardian = false,
): string {
  if (isArenaGuardian) {
    if (role === "harvester") return "Dread Harvester";
    return role === "warden" ? "Siege Warden" : "Arena Guardian";
  }
  if (!role) return "Unknown contact";
  const base = ENEMY_ROLES[role]?.name ?? role;
  if (isAce) return `Ace ${base}`;
  if (elite) return `Elite ${base}`;
  return base;
}

export function defaultAttackLabel(
  kind: DamageAttackKind,
  role: EnemyRoleId | null | undefined,
): string {
  switch (kind) {
    case "melee":
      if (role === "breacher") return "ram";
      if (role === "marauder") return "boarding strike";
      return "contact";
    case "mine":
      return "mine";
    case "volatile":
      return "volatile blast";
    case "projectile":
      if (role === "bomber") return "bomb";
      if (role === "turret") return "turret fire";
      if (role === "jammer") return "disruptor";
      if (role === "sniper") return "rail shot";
      if (role === "warden") return "siege volley";
      if (role === "harvester") return "crusher volley";
      return "bolts";
    default:
      return "hit";
  }
}

export function buildLastDamageSource(
  player: { x: number; y: number },
  damage: number,
  shieldAbsorbed: number,
  armorAbsorbed: number,
  hullDamage: number,
  source?: DamageSourceInput,
): LastDamageSource {
  const role = source?.role ?? null;
  const elite = Boolean(source?.elite);
  const isAce = Boolean(source?.isAce);
  const isArenaGuardian = Boolean(source?.isArenaGuardian);
  const attackKind = source?.attackKind ?? "unknown";
  const dist = source !== undefined ? Math.hypot(source.x - player.x, source.y - player.y) : 0;
  return {
    enemyRole: role,
    enemyName: enemyDisplayName(role, elite, isAce, isArenaGuardian),
    elite,
    isAce,
    attackKind,
    attackLabel: source?.attackLabel ?? defaultAttackLabel(attackKind, role),
    distanceM: dist,
    damage,
    shieldAbsorbed,
    armorAbsorbed,
    hullDamage,
  };
}

/** One-line killer blurb for death message / debrief hero. */
export function formatDeathHeadline(src: LastDamageSource): string {
  return `Downed by ${src.enemyName}`;
}

/** Compact secondary line: attack · range · damage split. */
export function formatDeathDetail(src: LastDamageSource): string {
  const range = src.distanceM < 1.5 ? "point-blank" : `${src.distanceM.toFixed(0)} m`;
  const parts = [`${src.attackLabel}`, range, `${Math.round(src.damage)} dmg`];
  const absorb: string[] = [];
  if (src.shieldAbsorbed > 0.5) absorb.push(`${Math.round(src.shieldAbsorbed)} shd`);
  if (src.armorAbsorbed > 0.5) absorb.push(`${Math.round(src.armorAbsorbed)} arm`);
  if (src.hullDamage > 0.5) absorb.push(`${Math.round(src.hullDamage)} hull`);
  if (absorb.length > 0) parts.push(absorb.join(" → "));
  return parts.join(" · ");
}

export function formatDeathMessage(src: LastDamageSource, grade: string): string {
  return `${formatDeathHeadline(src)} — ${formatDeathDetail(src)}. Grade ${grade}. Relaunch with R.`;
}
